import * as XLSX from 'xlsx';

// Size-wise PO import (e.g. "FUSION TENIM PO FORMAT"): an optional title row, then a
// header row with BUYER / PO NO. / Work Order Number / SKU Number / ... / COLOUR, one
// column per size, TOTAL QTY and Qty Per pack. Every filled size cell becomes its own
// SKU line. The standard template uses snake_case headers ("po_number") and never has
// a "PO NO." column, so finding one is what tells the two formats apart.

const HEADER_SCAN_ROWS = 10;

// Header text is matched with case, spaces and punctuation stripped ("PO NO." -> "pono").
const normalizeHeader = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const FIELD_HEADERS = {
  po_number: ['pono'],
  customer_name: ['buyer'],
  work_order: ['workordernumber', 'workorder'],
  sku_number: ['skunumber'],
  description: ['skudescription', 'description'],
  product_category: ['productcategory'],
  ship_date: ['shipdate'],
  color: ['colour', 'color'],
  total_qty: ['totalqty'],
  qty_per_pack: ['qtyperpack'],
};
type Field = keyof typeof FIELD_HEADERS;
const FIELDS = Object.keys(FIELD_HEADERS) as Field[];

const REQUIRED_FIELDS: { field: Field; label: string }[] = [
  { field: 'po_number', label: 'PO NO.' },
  { field: 'customer_name', label: 'BUYER' },
  { field: 'sku_number', label: 'SKU Number' },
];

export interface SizeWiseSku {
  sku_number: string;
  description: string;
  product_category: string;
  work_order: string;
  color: string;
  size: string;
  total_qty: number;
  qty_per_pack: number;
}

export interface SizeWisePo {
  po_number: string;
  customer_name: string;
  ship_date: string | null;
  skus: SizeWiseSku[];
}

export type SizeWiseResult = { pos: SizeWisePo[] } | { error: string };

const pad2 = (n: number) => String(n).padStart(2, '0');

// Excel date serial -> YYYY-MM-DD, in UTC so the browser's timezone can't shift the day.
const serialToIso = (serial: number) => {
  const d = new Date(Math.round((serial - 25569) * 86400000));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

// Returns null when the sheet isn't in the size-wise format, so the caller falls back
// to the standard import. Expects a workbook read without `cellDates`, so date cells
// keep their Excel serial number.
export const parseSizeWiseSheet = (
  ws: XLSX.WorkSheet | undefined,
  normalizeDate: (raw: any) => string | null,
): SizeWiseResult | null => {
  if (!ws || !ws['!ref']) return null;
  const range = XLSX.utils.decode_range(ws['!ref']);
  const cellAt = (r: number, c: number): XLSX.CellObject | undefined => ws[XLSX.utils.encode_cell({ r, c })];
  const textAt = (r: number, c: number) => {
    const cell = cellAt(r, c);
    return cell ? String(cell.w ?? cell.v ?? '').trim() : '';
  };

  let headerRow = -1;
  let columns: Partial<Record<Field, number>> = {};
  for (let r = range.s.r; r <= Math.min(range.e.r, range.s.r + HEADER_SCAN_ROWS - 1) && headerRow < 0; r++) {
    const found: Partial<Record<Field, number>> = {};
    for (let c = range.s.c; c <= range.e.c; c++) {
      const header = normalizeHeader(textAt(r, c));
      const field = FIELDS.find(f => FIELD_HEADERS[f].includes(header));
      if (field && found[field] === undefined) found[field] = c;
    }
    if (found.po_number !== undefined && found.total_qty !== undefined) {
      headerRow = r;
      columns = found;
    }
  }
  if (headerRow < 0) return null;

  const missing = REQUIRED_FIELDS.filter(({ field }) => columns[field] === undefined).map(({ label }) => label);
  if (missing.length > 0) return { error: `Missing required columns: ${missing.join(', ')}` };

  // Size columns: everything between the last known column before TOTAL QTY
  // (normally COLOUR) and TOTAL QTY itself, so any size run (8-18, S-XXL, 3XL…) works.
  const totalQtyCol = columns.total_qty!;
  const lastFieldBefore = Math.max(range.s.c - 1, ...Object.values(columns).filter((c): c is number => c !== undefined && c < totalQtyCol));
  const sizeCols: { col: number; size: string }[] = [];
  for (let c = lastFieldBefore + 1; c < totalQtyCol; c++) {
    const size = textAt(headerRow, c);
    if (size) sizeCols.push({ col: c, size });
  }

  const textOf = (r: number, field: Field) => (columns[field] === undefined ? '' : textAt(r, columns[field]!));
  const qtyAt = (r: number, c: number | undefined) => {
    const cell = c === undefined ? undefined : cellAt(r, c);
    return cell ? parseInt(String(cell.v).replace(/,/g, '')) || 0 : 0;
  };
  const shipDateAt = (r: number) => {
    const cell = columns.ship_date === undefined ? undefined : cellAt(r, columns.ship_date);
    if (cell?.t === 'n') return serialToIso(cell.v as number);
    return normalizeDate(textOf(r, 'ship_date'));
  };

  const pos = new Map<string, { po: SizeWisePo; row: number; shipDateText: string }>();
  for (let r = headerRow + 1; r <= range.e.r; r++) {
    const rowNumber = r + 1;
    const poNumber = textOf(r, 'po_number');
    const skuNumber = textOf(r, 'sku_number');
    // Blank rows and stray notes below the data have neither, and are skipped.
    if (!poNumber && !skuNumber) continue;
    if (!skuNumber) return { error: `Row ${rowNumber}: SKU Number is required.` };
    if (!poNumber) return { error: `Row ${rowNumber}: PO Number is required.` };

    const buyer = textOf(r, 'customer_name');
    const shipDate = shipDateAt(r);
    const shipDateText = textOf(r, 'ship_date');

    // Same rule as the standard import: every row of a PO must agree on its PO-level
    // details. Work order isn't one of them here — it's kept per SKU line.
    const group = pos.get(poNumber);
    if (group) {
      const conflict =
        group.po.customer_name !== buyer ? { label: 'BUYER', a: group.po.customer_name, b: buyer } :
        (group.po.ship_date || '') !== (shipDate || '') ? { label: 'Ship date', a: group.shipDateText, b: shipDateText } :
        null;
      if (conflict) {
        return { error: `PO "${poNumber}" has conflicting "${conflict.label}": "${conflict.a}" (row ${group.row}) vs "${conflict.b}" (row ${rowNumber}). All rows for the same PO must have identical PO-level details.` };
      }
    }

    const line = {
      sku_number: skuNumber,
      description: textOf(r, 'description'),
      product_category: textOf(r, 'product_category'),
      work_order: textOf(r, 'work_order'),
      color: textOf(r, 'color'),
      qty_per_pack: qtyAt(r, columns.qty_per_pack),
    };
    const sized = sizeCols
      .map(({ col, size }) => ({ size, total_qty: qtyAt(r, col) }))
      .filter(s => s.total_qty > 0);
    // A row with no size quantities still becomes one line, with a blank size and TOTAL QTY.
    const skus = sized.length > 0
      ? sized.map(s => ({ ...line, ...s }))
      : [{ ...line, size: '', total_qty: qtyAt(r, totalQtyCol) }];

    if (group) {
      group.po.skus.push(...skus);
    } else {
      pos.set(poNumber, {
        po: { po_number: poNumber, customer_name: buyer, ship_date: shipDate, skus },
        row: rowNumber,
        shipDateText,
      });
    }
  }

  if (pos.size === 0) return { error: 'File is empty or has no data rows.' };
  return { pos: [...pos.values()].map(g => g.po) };
};
